import { useState, useEffect, useCallback } from 'react';

import { pixelApi } from './pixelApi';
import { PixelConfigDTO } from './pixelApiTypes';

type UsePixelConfigOptions = {
  advertiserId: string | null | undefined;
  autoFetch?: boolean;
};

type UsePixelConfigReturn = {
  config: PixelConfigDTO | null;
  isLoading: boolean;
  error: string | null;
  fetchConfig: () => Promise<void>;
  saveConfig: (config: PixelConfigDTO) => Promise<boolean>;
  publishConfig: () => Promise<boolean>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
};

export const usePixelConfig = (options: UsePixelConfigOptions): UsePixelConfigReturn => {
  const { advertiserId, autoFetch = true } = options;

  const [config, setConfig] = useState<PixelConfigDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    if (!advertiserId) {
      setConfig(null);
      setError(null);

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await pixelApi.getPixelConfig(advertiserId);

      setConfig(data);
      setHasUnsavedChanges(false);
      setError(null);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      const status = axiosError?.response?.status;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        'Failed to fetch pixel config';

      // Handle 404 as success with null data (config doesn't exist yet)
      if (status === 404) {
        setConfig(null);
        setError(null);

        return;
      }

      // Handle 400 - Advertiser not found or advertiser does not have a pixel code
      if (status === 400) {
        if (
          errorMessage.includes('could not be found') ||
          errorMessage.includes('CTVAdvertiser') ||
          errorMessage.includes('does not have a pixel code') ||
          errorMessage.includes('has no pixelCode')
        ) {
          setConfig(null);
          setError(null);

          return;
        }
      }

      setError(errorMessage);
      setConfig(null);
    } finally {
      setIsLoading(false);
    }
  }, [advertiserId]);

  const saveConfig = useCallback(
    async (configToSave: PixelConfigDTO): Promise<boolean> => {
      if (!advertiserId) {
        setError('Advertiser ID is required');

        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await pixelApi.savePixelConfig(advertiserId, configToSave);

        setConfig(data);
        setHasUnsavedChanges(false);

        return true;
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { message?: string } }; message?: string };

        setError(
          axiosError?.response?.data?.message ||
            axiosError?.message ||
            'Failed to save pixel config',
        );

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [advertiserId],
  );

  const publishConfig = useCallback(async (): Promise<boolean> => {
    if (!advertiserId) {
      setError('Advertiser ID is required');

      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await pixelApi.publishPixelConfig(advertiserId);

      setConfig(data);
      setHasUnsavedChanges(false);

      return true;
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } }; message?: string };

      setError(
        axiosError?.response?.data?.message ||
          axiosError?.message ||
          'Failed to publish pixel config',
      );

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [advertiserId]);

  // Auto-fetch when advertiserId changes
  useEffect(() => {
    if (autoFetch && advertiserId) {
      fetchConfig();
    }
  }, [autoFetch, advertiserId, fetchConfig]);

  return {
    config,
    isLoading,
    error,
    fetchConfig,
    saveConfig,
    publishConfig,
    hasUnsavedChanges,
    setHasUnsavedChanges,
  };
};
